import { supabase } from '@/lib/supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeveloperProject {
  id: string;
  plan_code: 'free' | 'developer';
  monthly_request_limit: number;
  history_days: number;
  stripe_customer_id: string | null;
}

export interface ApiKeySummary {
  id: string;
  key_prefix: string;
  label: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
}

export interface CreateKeyResult {
  key: string;
  key_prefix: string;
  label: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function getAccessToken(): Promise<string> {
  if (!supabase) throw new Error('Supabase not configured');
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function authFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();

  // Route through Vercel rewrites (same origin) to avoid CORS issues
  // /api/v1/keys/* → manage-api-keys, /api/v1/stripe/* → stripe-webhook
  const res = await fetch(`/api/v1${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error((body as { error?: string }).error ?? `Request failed: ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// API Methods
// ---------------------------------------------------------------------------

export async function getProject(): Promise<DeveloperProject> {
  return authFetch<DeveloperProject>('/keys/project');
}

export async function listKeys(): Promise<ApiKeySummary[]> {
  const result = await authFetch<{ keys: ApiKeySummary[]; plan: string }>('/keys');
  return result.keys;
}

export async function createKey(label: string): Promise<CreateKeyResult> {
  return authFetch<CreateKeyResult>('/keys', {
    method: 'POST',
    body: JSON.stringify({ label: label || 'default' }),
  });
}

export async function revokeKey(keyId: string): Promise<void> {
  await authFetch<{ message: string }>(`/keys?id=${keyId}`, {
    method: 'DELETE',
  });
}

export async function createCheckoutSession(): Promise<string> {
  const result = await authFetch<{ url: string }>('/stripe?action=create-checkout', {
    method: 'POST',
  });
  return result.url;
}
